import { VideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';

import { formatSceneNarration } from 'js/src/agent/html-design/sceneNarration';
import {
    currentDesign,
    describeVideo,
    SCENE_HTML_RENDERING_RULES,
} from 'js/src/agent/html-design/sceneDesignFacts';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import {
    renderColorAsString,
    renderColorSlot,
} from 'js/src/agent/video-state/serialization/assetHelpers';
import { isWatermarkAsset } from 'js/src/agent/video-state/serialization/mediaHelpers';
import { buildTextColorMap } from 'js/src/agent/video-state/serialization/textColorMap';
import type { RootState } from 'js/src/libs/RootState';

/**
 * How a scene looks, from what it holds: its colour backgrounds and text colours with their slots,
 * and whether it shows footage or a photo. Read from the scene's data rather than a render, so it
 * costs nothing and says which slots the video uses for what.
 */
function describeLook(scene: Scene): string {
    const assets = scene.getAssets();
    const backgrounds = assets.flatMap((asset) =>
        VideoData.isColorAsset(asset)
            ? [
                  `${renderColorAsString(asset.sourceColor, false)}${renderColorSlot(asset, 'sourceColor')}`,
              ]
            : [],
    );
    const textColors = assets.flatMap((asset) =>
        VideoData.isTextAsset(asset)
            ? buildTextColorMap(asset)
                  .filter((handle) => handle.role === 'text')
                  .map((handle) => handle.value)
            : [],
    );
    const parts = [
        backgrounds.length > 0 ? `background ${[...new Set(backgrounds)].join(', ')}` : '',
        assets.some((asset) => VideoData.isMediaAsset(asset) && !isWatermarkAsset(asset))
            ? 'footage or a photo'
            : '',
        scene.getAssetsByType(VideoData.HTML_ASSET_TYPE).length > 0 ? 'a motion graphic' : '',
        textColors.length > 0 ? `text ${[...new Set(textColors)].join(', ')}` : '',
    ].filter(Boolean);
    return parts.length > 0 ? parts.join('; ') : 'nothing of its own';
}

/**
 * The scenes either side of the chart's, which it has to sit between: the brief describes them in a
 * sentence, and this says which colours they actually use, so the chart can tell a dark video from
 * a light one.
 */
function describeNeighbours(luminary: Luminary, sceneIds: string[]): string {
    const scenes = luminary.scenes;
    const own = scenes.flatMap((scene, index) => (sceneIds.includes(scene.id) ? [index] : []));
    if (own.length === 0) {
        return '';
    }
    const before = scenes[Math.min(...own) - 1];
    const after = scenes[Math.max(...own) + 1];
    const lines = [
        ...(before ? [`- Before yours: ${describeLook(before)}.`] : []),
        ...(after ? [`- After yours: ${describeLook(after)}.`] : []),
    ];
    return lines.length > 0
        ? `\n## The scenes around yours\nMatch them: a chart between dark scenes is dark too, and uses the slots they use for text.\n${lines.join('\n')}\n`
        : '';
}

/**
 * What makes a chart work as one scene of a video rather than a slide, from reviewers' notes on
 * designs made without it (too much on screen, rushed reveals, overlap). A budget rather than
 * adjectives: models agree with "one message per scene" and still label every value.
 */
function videoGuidance(narrated: boolean): string {
    const message = narrated
        ? "This scene plays under a voiceover (its words are below) that does the explaining. The screen shows what the words can't: the visual itself, plus at most three figures, ones the narration names. No sentences or captions on screen beyond a short title; never restate the narration."
        : "This scene is a moment in a video, not a slide: viewers can't pause to read. Show only what proves its one point, with a short title and at most three figures; no sentences or captions.";
    return `## Make it work as one scene of a video
- ${message}
- The brief's data is there so the chart is accurate, not a list to label. Let the rest read as shape (bar heights, a line's slope), and leave out axes, gridlines, legends and secondary stats unless the point needs them.
- Make that visual the largest thing on screen. Keep it rich in finish (display type for the hero figure, gradients) while sparse in content.
- Pace the reveal ${narrated ? 'to the narration ' : ''}across the whole scene, and hold the full picture for the last 1.5 seconds. Nothing overlaps.`;
}

function sceneNarration(luminary: Luminary, state: RootState, sceneIds: string[]): string {
    return sceneIds
        .map((sceneId) => luminary.scenes.find((candidate) => candidate.id === sceneId))
        .flatMap((scene) => (scene ? [formatSceneNarration(scene, state)] : []))
        .filter(Boolean)
        .join('\n\n');
}

/**
 * The whole system prompt of a chart subagent: the task, the renderer facts a model can't guess, the
 * video's frame, palette and fonts, what the voiceover says over the scene, and the design to revise
 * when there is one. One write, checked only that it runs: the guidance, not a review loop, is what
 * keeps the design fit for video.
 */
export function getChartScenePrompt(
    luminary: Luminary,
    state: RootState,
    sceneIds: string[],
): string {
    const narration = sceneNarration(luminary, state, sceneIds);
    const existing = currentDesign(luminary, sceneIds);
    return `You design a chart scene of a Lumen5 video. Lumen5 videos are a sequence of scenes; the scene you are given becomes a single animated HTML document that fills the whole frame for the scene's duration. Design it as a polished motion graphic of what you are asked for, so it belongs in this video.

## How a scene's HTML is rendered
${SCENE_HTML_RENDERING_RULES}

${videoGuidance(Boolean(narration))}

## The video
${describeVideo(luminary, sceneIds)}
${describeNeighbours(luminary, sceneIds)}${narration ? `\n${narration}\n` : ''}${existing ? `\n## The scene's current design\nThe brief asks for changes to this. Keep what it doesn't mention.\n${existing}\n` : ''}
## What to do
Call \`${TOOL_NAMES.writeSceneHtml}\` with the complete document. It checks only that the scene runs; if it doesn't, you'll be told why and can write it once more.
`;
}
